import { err, ok, Result } from '@easeltv/fail';
import { ZodType, ZodTypeDef } from 'zod';

import {
    ResponseSchemaInvalidFail,
    ResponseStatusInvalidFail,
} from '../errors';
import { ApiErrorResponse } from '../models';
import { validateSchema } from '../utils/helpers';
import { NonValidatedResponse, ValidatedResponse } from '../utils/types';

/**
 * validateResponse
 *
 * Acts as a guard and checks if a NonValidatedResponse conforms to the type defined in the validator
 *
 * @param response
 * @param validator
 */
export const validateResponse = <
    Output,
    Def extends ZodTypeDef = ZodTypeDef,
    Input = Output,
>(
    response: NonValidatedResponse,
    validator: ZodType<Output, Def, Input>,
): Result<
    ValidatedResponse<Output>,
    ResponseStatusInvalidFail | ResponseSchemaInvalidFail
> => {
    const { status, body } = response;

    // Checks for valid status
    // @TODO: Add ability to check for custom statuses rather than 200's
    if (status > 299 || status < 200) {
        const validatedErrorResult = validateSchema(ApiErrorResponse, body);

        if (!validatedErrorResult.ok) {
            return err(
                new ResponseStatusInvalidFail({
                    name: 'Response Status Error',
                    description: 'Status of the response is invalid',
                    key: 'remote.response.status',
                    data: {
                        response,
                    },
                    status,
                }),
            );
        }

        return err(
            new ResponseStatusInvalidFail({
                name: validatedErrorResult.payload.name,
                description: validatedErrorResult.payload.name,
                key: validatedErrorResult.payload.key,
                data: validatedErrorResult.payload.data,
                status,
            }),
        );
    }

    // Run the non-validated data through the zod parser
    // Use safeParse as it returns an error object rather than throwing an error
    const validateSchemaResult = validateSchema(validator, body);

    // Unsuccessful parses return an error object
    if (!validateSchemaResult.ok) return validateSchemaResult;

    // Successful parse will return ValidatedResponse
    return ok({
        status,
        body: validateSchemaResult.payload,
    });
};
